import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ERelationType,
  EEntityType,
  ITicketCategory,
  ETicketType,
} from '@web/dtos';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { map, Observable } from 'rxjs';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import {
  IDialogData,
  IDialogDataWithTicketType,
} from '../../../components/dialogs/dialog';
import { TicketCategoryFormsFactoryService } from '../../../factory/ticket-category/ticket-category-factory.service';
import { ITicketCategoryFormWrapper } from '../../../entities/ticket-category.entity';
import { TicketCategoryAddDialogComponent } from '../../../components/dialogs/ticket-category-add-dialog/ticket-category-add-dialog.component';
import { TicketCategoryDragAndDropListComponent } from '../../../components/data-display/category-drag-and-drop-list/ticket-category-drag-and-drop-list.component';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPreview,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { UnassignedTicketsDragAndDropListComponent } from '../../../components/data-display/unassigned-tickets-drag-and-drop-list/unassigned-tickets-drag-and-drop-list.component';
import { CmsTicketCategoryService } from '../../../services/cms-ticket-category/cms-ticket-category.service';

@Component({
  selector: 'aw-ticket-categories',
  imports: [
    CommonModule,
    SectionComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    ReactiveFormsModule,
    ButtonComponent,
    SectionHeaderActionsComponent,
    LoadingAnimationComponent,
    TicketCategoryDragAndDropListComponent,
    CdkDropList,
    CdkDrag,
    CdkDragPreview,
    CdkScrollable,
    UnassignedTicketsDragAndDropListComponent,
  ],
  templateUrl: './ticket-categories.component.html',
  styles: '',
})
export class TicketCategoriesComponent implements OnInit {
  protected readonly cmsTicketCategoryService: CmsTicketCategoryService =
    inject(CmsTicketCategoryService);
  public readonly ticketCategoryFactory: TicketCategoryFormsFactoryService =
    inject(TicketCategoryFormsFactoryService);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly dialogService: DialogService = inject(DialogService);
  readonly filterTexts: FilterTextsPipe = inject(FilterTextsPipe);
  public ticketCategoryForm: FormGroup<ITicketCategoryFormWrapper> =
    this.ticketCategoryFactory.buildForm(
      this.cmsTicketCategoryService.ticketCategories()
    );

  protected readonly TextRelationType: typeof ERelationType = ERelationType;

  protected readonly EntityType: typeof EEntityType = EEntityType;

  public isCategoryBeingDragged: WritableSignal<boolean> = signal(false);

  constructor() {
    effect((): void => {
      this.ticketCategoryForm = this.ticketCategoryFactory.buildForm(
        this.cmsTicketCategoryService.ticketCategories()
      );
    });
  }

  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.cmsTicketCategoryService.isDirty()) {
        event.preventDefault();
      }
    };
  }

  save(): boolean {
    if (!this.ticketCategoryForm.valid) {
      this.ticketCategoryForm.markAllAsTouched();
      return false;
    }

    void this.cmsTicketCategoryService.save(
      this.ticketCategoryFactory.buildEntity(this.ticketCategoryForm)
    );

    return true;
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        icon: 'warning',
        iconBackgroundColor: '#eed202',
        width: '600px',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  resetFormChanges(): void {
    this.cmsTicketCategoryService.resetChanges();
  }

  handleAddTicketCategoryDialog(ticketType: ETicketType) {
    this.dialogService.openDialog<IDialogDataWithTicketType>(
      TicketCategoryAddDialogComponent,
      {
        headline: this.cmsTextProvider.ticketCategories.addCategory,
        width: '400px',
        ticketType: ticketType,
      }
    );
  }

  public dropCategory(event: CdkDragDrop<ITicketCategory[]>): void {
    if (
      !event.isPointerOverContainer ||
      event.previousIndex === event.currentIndex
    ) {
      return;
    }

    this.cmsTicketCategoryService.moveTicketCategory(
      event.item.data.ticketType,
      event.previousIndex,
      event.currentIndex
    );
  }

  protected readonly ETicketType: typeof ETicketType = ETicketType;
}
